
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/shared/ui/dialog";
import { Button } from "@/shared/ui/button";
import { Textarea } from "@/shared/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/shared/ui/radio-group";
import { Label } from "@/shared/ui/label";
import { Badge } from "@/shared/ui/badge";
import { Loader2, Search, User } from "lucide-react";
import { useMemo, useState } from "react";
import { useAddTerminalToStarlink, useAddTerminalToOrganization } from "@/domains/terminals/services/user-terminals";
import { useOrganizations } from "@/domains/accounts/services/accounts";
import { ERROR } from "@/config/toast-message-config";
import { showErrorWithApiError, showError, showSuccess } from "@/shared/utils/toast-utils";
import EmptyView from "@/shared/components/EmptyView";
import { TerminalType } from "@/shared/types/userTerminalTypes";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/ui/select";
import SearchBar from "@/shared/components/SearchBar";
import { TERMINAL_TYPE_INFO } from "@/shared/utils/color-utils-terminals";

interface AddTerminalsToStarlinkDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  selectedRegionCode: string;
}

const AddTerminalsToStarlinkDialog = ({
  open,
  onOpenChange,
  onSuccess,
  selectedRegionCode,
}: AddTerminalsToStarlinkDialogProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState("");
  const [deviceIdsInput, setDeviceIdsInput] = useState('');
  const [selectedTerminalType, setSelectedTerminalType] = useState<TerminalType | null>(null);

  const { mutateAsync: addTerminalToStarlink } = useAddTerminalToStarlink();
  const { mutateAsync: addTerminalToOrganization } = useAddTerminalToOrganization();
  const { data: organizations, isLoading: isLoadingOrganizations } = useOrganizations(open, selectedRegionCode);

  const enum AddTerminalStep {
    Starlink = 'starlink',
    Account = 'account',
  }

  // Parse device IDs from input string (comma, semicolon, or newline separated)
  const parseDeviceIds = (input: string): string[] => {
    return input
      .split(/[,;\n]/)
      .map(id => id.trim())
      .filter(id => id.length > 0);
  };

  // Get parsed device IDs for display
  const parsedDeviceIds = parseDeviceIds(deviceIdsInput);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const deviceIds = parseDeviceIds(deviceIdsInput);

    if (deviceIds.length === 0) {
      showError(ERROR.TERMINAL.DEVICE_IDS_REQUIRED);
      return;
    }

    setIsLoading(true);

    let step = AddTerminalStep.Starlink;
    let successCount = 0;
    let errorCount = 0;

    try {
      // Process each device ID
      for (const deviceId of deviceIds) {
        try {
          // First add terminal to Starlink
          await addTerminalToStarlink({ regionCode: selectedRegionCode, deviceId, type: selectedTerminalType });
          successCount++;

          // If an organization is selected, also add terminal to organization
          if (selectedOrganizationId) {
            step = AddTerminalStep.Account;
            try {
              await addTerminalToOrganization({
                organizationId: selectedOrganizationId,
                terminalId: deviceId
              });
            } catch (error) {
              showErrorWithApiError(error, ERROR.TERMINAL.ADD_TO_ACCOUNT);
              errorCount++;
            }
          }
        } catch (error) {
          if (step === AddTerminalStep.Account) {
            showErrorWithApiError(error, ERROR.TERMINAL.ADD_TO_ACCOUNT);
          } else {
            showErrorWithApiError(error, ERROR.TERMINAL.ADD_TO_STARLINK);
          }
          errorCount++;
        }
      }

      // Show summary message
      if (successCount > 0) {
        showSuccess(`Successfully added ${successCount} terminal(s) to Starlink${selectedOrganizationId ? ' and assigned to organization' : ''}`);
      }

      if (errorCount > 0) {
        showError(`${errorCount} terminal(s) failed to be added`);
      }

      if (successCount > 0) {
        onSuccess();
        onOpenChange(false);
        resetDialog();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const resetDialog = () => {
    setSelectedOrganizationId('');
    setSearchTerm("");
    setDeviceIdsInput('');
  };

  const handleDialogClose = (open: boolean) => {
    if (!open) {
      resetDialog();
    }
    onOpenChange(open);
  };

  // Filter accounts based on search term
  const filteredOrganizations = useMemo(() => {
    return organizations?.filter(organization =>
      organization.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      organization.organizationId.toLowerCase().includes(searchTerm.toLowerCase())
    ) || [];
  }, [organizations, searchTerm]);

  const showSearch = organizations && organizations.length > 0;

  return (
    <Dialog open={open} onOpenChange={handleDialogClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Add New Terminal(s) to Starlink and assign to an account</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-4">
            {/* Device IDs Input */}
            <div className="space-y-2">
              <Label htmlFor="deviceIds">Device IDs</Label>
              <Textarea
                id="deviceIds"
                placeholder="Enter Device IDs separated by commas, semicolons, or new lines&#10;Example: DEVICE001, DEVICE002, DEVICE003"
                value={deviceIdsInput}
                onChange={(e) => setDeviceIdsInput(e.target.value)}
                rows={4}
                className="resize-none"
              />
              <p className="text-xs">
                Separate multiple Device IDs with commas (,), semicolons (;), or new lines
              </p>

              {/* Show parsed terminals preview */}
              {parsedDeviceIds.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium">
                    Preview ({parsedDeviceIds.length} terminal{parsedDeviceIds.length > 1 ? 's' : ''})
                  </Label>
                  <div className="flex flex-wrap gap-1 max-h-[100px] overflow-y-auto border rounded-md p-2">
                    {parsedDeviceIds.map((deviceId, index) => (
                      <Badge key={index} variant="secondary" className="text-xs">
                        {deviceId}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Terminal Type Selection Section */}
            <div className="space-y-4">
              <div>
                <h4 className="font-medium mb-2">Terminal Type (Optional)</h4>
                <p className="text-sm mb-4">
                  Optionally select the terminal type for all added terminals.
                </p>
              </div>
              <Select
                value={selectedTerminalType ?? undefined}
                onValueChange={(value) => setSelectedTerminalType(value as TerminalType)}
                disabled={isLoading}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type (optional)" />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(TerminalType).map((option) => (
                    <SelectItem key={option} value={option}>
                      {TERMINAL_TYPE_INFO[option].value}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Account Selection Section */}
            <div className="space-y-4">
              <div>
                <h4 className="font-medium mb-2">Add to Organization (Optional)</h4>
                <p className="text-sm mb-4">
                  Optionally assign this terminal to an organization.
                </p>
              </div>

              {/* Search Input - only show when there are accounts */}
              {showSearch && (
                <div className="relative">
                  <SearchBar value={searchTerm} onChange={(value) => setSearchTerm(value)} placeholder="Search accounts..." />
                </div>
              )}

              {isLoadingOrganizations ? (
                <div className="flex justify-center py-4">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : filteredOrganizations.length === 0 ? (
                searchTerm ? (
                  <EmptyView
                    icon={Search}
                    size="small"
                    title="No Accounts Found"
                    description={`No accounts match "${searchTerm}". Try adjusting your search.`}
                  />
                ) : (
                  <EmptyView
                    icon={User}
                    size="small"
                    title="No Accounts Available"
                    description="There are no accounts available to add this terminal to."
                  />
                )
              ) : (
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  <RadioGroup
                    value={selectedOrganizationId}
                    onValueChange={setSelectedOrganizationId}
                    className="space-y-2"
                  >
                    {filteredOrganizations.map((organization) => (
                      <div key={organization.organizationId} className="flex items-center space-x-2">
                        <RadioGroupItem
                          value={organization.organizationId}
                          id={`organization-${organization.organizationId}`}
                        />
                        <Label
                          htmlFor={`organization-${organization.organizationId}`}
                          className="text-sm font-normal cursor-pointer flex-1"
                        >
                          <div className="flex flex-col">
                            <span>{organization.name}</span>
                            <span className="text-xs">ID: {organization.organizationId}</span>
                          </div>
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              className="gap-2"
              onClick={() => handleDialogClose(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading || parsedDeviceIds.length === 0}
              className="gap-2">
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Adding Terminals...
                </>
              ) : (
                `Add ${parsedDeviceIds.length > 0 ? `${parsedDeviceIds.length} Terminal${parsedDeviceIds.length > 1 ? 's' : ''}` : 'Terminals'}`
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default AddTerminalsToStarlinkDialog; 